import {Country} from "countries-list";
import TomSelect from "tom-select";
import CaptchaInitializer from "../../../Scalesoft.Shared.FrontEnd/Scripts/Captcha/CaptchaInitializer";
import {NotificationType} from "../../../Scalesoft.Shared.FrontEnd/Scripts/Enums/NotificationType";
import ErrorHandler from "../../../Scalesoft.Shared.FrontEnd/Scripts/Error-Handler/ErrorHandler";
import {KeyValuePair} from "../../../Scalesoft.Shared.FrontEnd/Scripts/Global/KeyValuePair";
import GuiCommonShared from "../../../Scalesoft.Shared.FrontEnd/Scripts/GuiCommonShared";
import NotificationConfiguration from "../../../Scalesoft.Shared.FrontEnd/Scripts/NotificationConfiguration";
import {ContactConfigurationTypeEnum} from "../Enums/ContactConfigurationTypeEnum";
import {ContactVerificationType} from "../Enums/ContactVerificationType";
import {MethodContactType} from "../Enums/MethodContactType";
import InputFormat from "../InputFormat";
import ApiClient from "../Registration/ApiClient";
import IContact from "../Registration/Interfaces/IContact";
import IEmailViewModel from "../Registration/Interfaces/IEmailViewModel";
import IPhoneNumberViewModel from "../Registration/Interfaces/IPhoneViewModel";
import IPostalAddressViewModel from "../Registration/Interfaces/IPostalAddressViewModel";
import ContactApiClient from "./ContactApiClient";
import ContactCard from "./ContactCard";
import ContactConfigurationProvider from "./ContactConfigurationProvider";
import ContactUtils from "./ContactUtils";
import {ContactType} from "./Enums/ContactType";

// tslint:disable:no-var-requires
const autocomplete = require("autocomplete.js"); // TODO Upgrade autocomplete #IN2-7397
const countries = require("countries-list/dist/countries.min.json") as { [key: string]: Country };
// tslint:enable:no-var-requires

declare const methodType: MethodContactType;

export default class ContactList {
    private readonly guiCommon: GuiCommonShared;
    private readonly registerFormEl: JQuery;
    private readonly emailsRow: JQuery;
    private readonly phoneNumberRow: JQuery;
    private readonly postalAddressesRow;
    private readonly contactDialogsContainer: JQuery;
    private readonly warningAlertElement: JQuery;
    private readonly apiClient: ApiClient;
    private readonly contactClient: ContactApiClient;
    private readonly captchaInitializer: CaptchaInitializer;
    private readonly contactConfigurationProvider: ContactConfigurationProvider;
    private readonly formatInput: InputFormat;
    private readonly formValidator: JQueryValidation.Validator;
    private readonly errorHandler: ErrorHandler;
    private readonly mLocalization: Localization;
    private emailList: IContact[];
    private phoneNumberList: IContact[];
    private readonly mOnCardAdded: (cardEl: JQuery) => void;
    private readonly cardViewModelPrefix: string;
    private readonly createContactsOnFormSubmit: boolean;
    private deletedContactInput: JQuery;

    public constructor(parentElement: JQuery,
                       contactsDialogContainer: JQuery,
                       captchaInitializer: CaptchaInitializer,
                       errorHandler: ErrorHandler,
                       guiCommon: GuiCommonShared,
                       localization: Localization,
                       cardViewModelPrefix: string,
                       onCardAdded: (cardEl: JQuery) => void = null,
                       createContactsOnFormSubmit: boolean = false,
    ) {
        this.mLocalization = localization;
        this.registerFormEl = parentElement;
        this.contactDialogsContainer = contactsDialogContainer;
        this.emailsRow = this.registerFormEl.find(".email-row");
        this.phoneNumberRow = this.registerFormEl.find(".phoneNumber-row");
        this.postalAddressesRow = this.registerFormEl.find(".postalAddress-row");
        this.deletedContactInput = this.registerFormEl.find(".contacts-to-delete");
        this.warningAlertElement = jq(".contacts-warning-alerts");

        this.createContactsOnFormSubmit = createContactsOnFormSubmit;
        this.cardViewModelPrefix = cardViewModelPrefix;

        this.apiClient = new ApiClient();
        this.formValidator = this.registerFormEl.data("validator");
        this.formatInput = new InputFormat(this.mLocalization);
        this.contactClient = new ContactApiClient();
        this.captchaInitializer = captchaInitializer;
        this.guiCommon = guiCommon;
        this.errorHandler = errorHandler;
        this.mOnCardAdded = onCardAdded;

        this.contactConfigurationProvider = new ContactConfigurationProvider();

        this.emailList = [];
        this.phoneNumberList = [];

        this.initModalForms();
        this.initContactCards();
        this.initErrorHandling();

        this.formatInput.formatPhoneNumber(contactsDialogContainer);
        this.formatInput.formatPostalCode(contactsDialogContainer);
        const countryInputContainer = contactsDialogContainer.find(".country-input").get(0);
        autocomplete(countryInputContainer, {
            // debug: true,
            hint: false,
        }, [
            {
                source: (query, callback) => {
                    const matchingCountryEntries = Object.entries(countries).filter((countryEntry) => {
                        const lowercaseQuery = query.toLocaleLowerCase();
                        const codeMatching = countryEntry[0].toLocaleLowerCase().includes(lowercaseQuery);
                        const nameMatching = countryEntry[1].name.toLocaleLowerCase().includes(lowercaseQuery);
                        const nativeNameMatching = countryEntry[1].native.toLocaleLowerCase().includes(lowercaseQuery);
                        return codeMatching || nameMatching || nativeNameMatching;
                    });

                    callback(matchingCountryEntries);
                },
                templates: {
                    suggestion: (term) => {
                        return `<div class="suggested-country"><span class='country-flag fi fi-${term[0].toLowerCase()}'></span> ${term[1].native}</div>`;
                    },
                },
                displayKey: (term) => term[1].native,
            },
        ]);
    }

    public validateContactType(contactType: ContactType): boolean {
        const contactValidationConfig = this.contactConfigurationProvider.getContactConfiguration();
        if (contactValidationConfig === null || !contactValidationConfig[contactType]) {
            return true;
        }

        let row;
        if (contactType === ContactType.Email) {
            row = this.emailsRow;
        }
        if (contactType === ContactType.PhoneNumber) {
            row = this.phoneNumberRow;
        }

        const errorContainer = jq(`#${contactType}ListErrors`);
        let isListValid: boolean = true;

        const contactTiles = row.children(".tile:not(.add-contact)");
        const numberOfTiles = contactTiles.length;

        // Contacts of somebody else are left out of every requirement that speaks about verification,
        // the same way the server does it in ContactConfigurationValidator.
        const tilesToVerify = contactTiles.filter((index, element) =>
            ContactUtils.groupRequiresVerification(jq(element).find(".contact-group").val() as string));
        const numberOfTilesToVerify = tilesToVerify.length;

        const numberOfVerifiedContacts = tilesToVerify.filter((index, element) => {
            const selectedForVerificationCheckbox = jq(element).find(".selected-for-verification-selector");
            const verifiedCheckbox = jq(element).find(".verified-selector");
            return selectedForVerificationCheckbox.is(":checked") || verifiedCheckbox.is(":checked");
        }).length;

        switch (contactValidationConfig[contactType].contactRequirement) {
            case ContactConfigurationTypeEnum.OneRequired:
                isListValid = numberOfTiles > 0;
                if (!isListValid) {
                    this.mLocalization.translateFormatAsync("required-minimum-contact", ["1"], "UserContactsViewModel")
                        .then((minimumRequiredTranslation) => {
                            errorContainer.html(minimumRequiredTranslation.result.value);
                        });
                }
                break;

            case ContactConfigurationTypeEnum.OneVerified:
                isListValid = numberOfVerifiedContacts > 0;
                if (!isListValid) {
                    this.mLocalization.translateFormatAsync("verified-minimum-contact", ["1"], "UserContactsViewModel")
                        .then((minimumVerifiedTranslation) => {
                            errorContainer.html(minimumVerifiedTranslation.result.value);
                        });
                }
                break;

            case ContactConfigurationTypeEnum.AllVerified:
                isListValid = numberOfTilesToVerify === numberOfVerifiedContacts;
                if (!isListValid) {
                    this.mLocalization.translateAsync("verified-all-contacts", "UserContactsViewModel")
                        .then((allVerifiedTranslation) => {
                            errorContainer.html(allVerifiedTranslation.result.value);
                        });
                }
                break;

            case ContactConfigurationTypeEnum.OneRequiredAllVerified:
                isListValid = (numberOfVerifiedContacts === numberOfTilesToVerify && numberOfTilesToVerify > 0);
                if (!isListValid) {
                    this.mLocalization.translateFormatAsync("required-one-verified-all-contacts", ["1"], "UserContactsViewModel")
                        .then((oneRequiredAllVerifiedTranslation) => {
                            errorContainer.html(oneRequiredAllVerifiedTranslation.result.value);
                        });
                }
                break;
        }

        if (isListValid) {
            errorContainer.empty();
        }

        return isListValid;
    }

    private getEmailCard(emailModel: IEmailViewModel, addEmailModal: JQuery, alertContainer: JQuery): void {
        const existingContacts = this.emailsRow.children(".tile:not(.add-contact)");
        const lastItemIndex = this.getLastItemIndexInRow(existingContacts);
        const ajax = this.contactClient.getEmailCard(emailModel, lastItemIndex, this.cardViewModelPrefix);
        ajax.done((response) => {
            const responseJq = jq(response);

            const contactCard = new ContactCard(
                ContactType.Email,
                responseJq,
                this.registerFormEl,
                this.contactDialogsContainer,
                this.apiClient,
                this.formatInput,
                lastItemIndex,
                this.contactClient,
                this.captchaInitializer,
                this.onContactValidationCallback.bind(this),
                this.contactCountUpdateCallback.bind(this),
                this.guiCommon,
                this.errorHandler,
                this.mLocalization,
                this.deleteFromContactList.bind(this),
                null,
                this.cardViewModelPrefix,
                this.createContactsOnFormSubmit,
                this.addContactIdToDeleted.bind(this),
                this.mOnCardAdded,
            );
            emailModel.id = this.getContactId(responseJq);
            contactCard.setEmailModel(emailModel);
            contactCard.insertCard();
            emailModel.index = responseJq.index();
            contactCard.init();
            addEmailModal.find("input").val("");
            this.contactCountUpdateCallback(ContactType.Email, this.emailsRow, response);
            addEmailModal.modal("hide");
            this.addContactToList(this.emailList, emailModel.email, emailModel.id, emailModel.index, emailModel.isContactVerified,
                emailModel.group, emailModel.name);
        });

        ajax.fail((response) => {
            addEmailModal.find(".modal-body .alert-container").prepend(`<span class="text-danger">${response}</span>`);
        });

        ajax.always(() => {
            this.guiCommon.destroyNotification(alertContainer);
            ContactUtils.handleLoading(alertContainer, false, ".loading", ".submit-modal-form-btn");
        });
    }

    private getPhoneCard(phoneModel: IPhoneNumberViewModel, addPhoneModal: JQuery, alertContainer: JQuery): void {
        const existingContacts = this.phoneNumberRow.children(".tile:not(.add-contact)");
        const lastItemIndex = this.getLastItemIndexInRow(existingContacts);
        const ajax = this.contactClient.getPhoneCard(phoneModel, lastItemIndex, this.cardViewModelPrefix);
        ajax.done((response) => {
            const responseJq = jq(response);

            const contactCard = new ContactCard(
                ContactType.PhoneNumber,
                responseJq,
                this.registerFormEl,
                this.contactDialogsContainer,
                this.apiClient,
                this.formatInput,
                lastItemIndex,
                this.contactClient,
                this.captchaInitializer,
                this.onContactValidationCallback.bind(this),
                this.contactCountUpdateCallback.bind(this),
                this.guiCommon,
                this.errorHandler,
                this.mLocalization,
                this.deleteFromContactList.bind(this),
                null,
                this.cardViewModelPrefix,
                this.createContactsOnFormSubmit,
                this.addContactIdToDeleted.bind(this),
                this.mOnCardAdded,
            );
            phoneModel.id = this.getContactId(responseJq);
            contactCard.setPhoneNumberModel(phoneModel);
            contactCard.insertCard();
            phoneModel.index = responseJq.index();
            contactCard.init();
            addPhoneModal.find("input").val("");
            this.contactCountUpdateCallback(ContactType.PhoneNumber, this.phoneNumberRow, response);
            addPhoneModal.modal("hide");
            this.addContactToList(this.phoneNumberList, phoneModel.phoneNumber, phoneModel.id, phoneModel.index,
                phoneModel.isContactVerified, phoneModel.group, phoneModel.name);
        });
        ajax.fail((response) => {
            addPhoneModal.find(".modal-body .alert-container").prepend(`<span class="text-danger">${response}</span>`);
        });

        ajax.always(() => {
            this.guiCommon.destroyNotification(alertContainer);
            ContactUtils.handleLoading(alertContainer, false, ".loading", ".submit-modal-form-btn");
        });
    }

    private initModalForms(): void {
        this.contactDialogsContainer.find("input").on("input", (event) => {
            const notificationContainer = jq(event.target).parents(".modal-body").find(".alert-container");
            this.guiCommon.destroyNotification(notificationContainer);
        });
        this.addEmailModalInit();
        this.addPhoneModalInit();
        this.addPostalAddressModalInit();

        const modals = this.contactDialogsContainer.find(".modal");
        modals.on("hidden.bs.modal", (event) => {
            modals.find(".error-container").empty();
            this.guiCommon.destroyNotification(jq(".alert-container"));
            this.setValidationSettings(".ignore-validation");
            const eventTargetJq = jq(event.target);
            eventTargetJq.find("input").val("");
            eventTargetJq.find("select").each((index, element) => {
                const select = jq(element);
                const firstOption = select.find("option").first();
                const tomselectInstance = (element as HTMLSelectElement & { tomselect: TomSelect }).tomselect;
                if (tomselectInstance) {
                    tomselectInstance.addItem(firstOption.val().toString());
                } else {
                    firstOption.prop("selected", true);
                }
            });
            ContactUtils.handleLoading(eventTargetJq, false, ".loading", ".submit-modal-form-btn");
        });
        modals.on("shown.bs.modal", () => {
            modals.find(".error-container").empty();
            this.setValidationSettings("");
        });
    }

    private createAddressCard(
        postalAddressViewModel: IPostalAddressViewModel,
        lastAddressIndex: number,
        addressModalJq: JQuery,
    ): void {
        const cardJq = this.postalAddressesRow.find(".postal-card-template .tile").clone();

        this.mLocalization.translateAsync(postalAddressViewModel.type, "address-type")
            .then((typeTranslation) => {

                cardJq.find(".contact-type").text(typeTranslation.result.value)
                    .val(postalAddressViewModel.type);
            });

        cardJq.find(".contact-name").text(postalAddressViewModel.name).val(postalAddressViewModel.name);
        cardJq.find(".contact-street").text(postalAddressViewModel.street).val(postalAddressViewModel.street);
        cardJq.find(".contact-code").text(postalAddressViewModel.postalCode).val(postalAddressViewModel.postalCode);
        cardJq.find(".contact-city").text(postalAddressViewModel.city).val(postalAddressViewModel.city);
        cardJq.find(".contact-country").text(postalAddressViewModel.country).val(postalAddressViewModel.country);
        cardJq.find(".address-id").val(postalAddressViewModel.id);

        const contactCard = new ContactCard(
            ContactType.Address,
            cardJq,
            this.registerFormEl,
            this.contactDialogsContainer,
            this.apiClient,
            this.formatInput,
            lastAddressIndex,
            this.contactClient,
            this.captchaInitializer,
            this.onContactValidationCallback.bind(this),
            this.contactCountUpdateCallback.bind(this),
            this.guiCommon,
            this.errorHandler,
            this.mLocalization,
            this.deleteFromContactList.bind(this),
            this.displayRefreshFailWarning.bind(this),
            this.cardViewModelPrefix,
            this.createContactsOnFormSubmit,
            this.addContactIdToDeleted.bind(this),
            null,
        );
        contactCard.setPostalAddressModel(postalAddressViewModel);
        contactCard.insertCard();
        contactCard.init();
        this.contactCountUpdateCallback(ContactType.Address, this.postalAddressesRow, null);

    }

    private setValidationSettings(ignoredInputsSelector: string): void {
        if (this.formValidator !== undefined) {
            this.formValidator.settings.ignore = ignoredInputsSelector;
        }
    }

    private getContactId(html: JQuery): number {
        const id = html.find(".contact-id").val() as string;
        return parseInt(id, 10);
    }

    private getLastItemIndexInRow(contactsRow: JQuery): number {
        let lastItemIndex;
        if (contactsRow.length !== 0) {
            lastItemIndex = contactsRow.index(contactsRow.last()) + 1;
        } else {
            lastItemIndex = 0;
        }

        return lastItemIndex;
    }

    private onContactValidationCallback(contactType: ContactType, oldId: number, newId: number, response: JQuery.jqXHR): boolean {
        if (contactType === ContactType.Email) {
            this.updateContactIdInList(this.emailList, newId, oldId);
        }
        if (contactType === ContactType.PhoneNumber) {
            this.updateContactIdInList(this.phoneNumberList, newId, oldId);
        }

        this.displayRefreshFailWarning(response);

        return this.validateContactType(contactType);
    }

    private displayRefreshFailWarning(response?): void {
        if (response && response.warningMessage) {
            const warningNotification = new NotificationConfiguration({
                notificationType: NotificationType.Warning,
                messages: [response.warningMessage],
                isDismissible: true,
                container: this.warningAlertElement,
            });

            this.guiCommon.showNotification(warningNotification);
        }
    }

    private initContactCards(): void {
        this.emailsRow.children(".tile:not(.add-contact)").each((index, element) => {
            const elementJq = jq(element);
            const contactCard = new ContactCard(
                ContactType.Email,
                elementJq,
                this.registerFormEl,
                this.contactDialogsContainer,
                this.apiClient,
                this.formatInput,
                index,
                this.contactClient,
                this.captchaInitializer,
                this.onContactValidationCallback.bind(this),
                this.contactCountUpdateCallback.bind(this),
                this.guiCommon,
                this.errorHandler,
                this.mLocalization,
                this.deleteFromContactList.bind(this),
                null,
                this.cardViewModelPrefix,
                this.createContactsOnFormSubmit,
                this.addContactIdToDeleted.bind(this),
                this.mOnCardAdded,
            );

            const model: IEmailViewModel = this.fillModelOnInit(elementJq, index, false) as IEmailViewModel;

            contactCard.setEmailModel(model);
            contactCard.init();

            this.addContactToList(this.emailList, model.email, model.id, index, model.isContactVerified, model.group, model.name);
        });

        this.phoneNumberRow.children(".tile:not(.add-contact)").each((index, element) => {
            const elementJq = jq(element);
            const contactCard = new ContactCard(
                ContactType.PhoneNumber,
                elementJq,
                this.registerFormEl,
                this.contactDialogsContainer,
                this.apiClient,
                this.formatInput,
                index,
                this.contactClient,
                this.captchaInitializer,
                this.onContactValidationCallback.bind(this),
                this.contactCountUpdateCallback.bind(this),
                this.guiCommon,
                this.errorHandler,
                this.mLocalization,
                this.deleteFromContactList.bind(this),
                null,
                this.cardViewModelPrefix,
                this.createContactsOnFormSubmit,
                this.addContactIdToDeleted.bind(this),
                this.mOnCardAdded,
            );

            const model: IPhoneNumberViewModel = this.fillModelOnInit(elementJq, index, true) as IPhoneNumberViewModel;

            contactCard.setPhoneNumberModel(model);
            contactCard.init();

            this.addContactToList(this.phoneNumberList, model.phoneNumber, model.id, index, model.isContactVerified, model.group, model.name);
        });

        this.postalAddressesRow.children(".tile:not(.add-contact)").each((index, element) => {
            const elementJq = jq(element);
            const contactCard = new ContactCard(
                ContactType.Address,
                elementJq,
                this.registerFormEl,
                this.contactDialogsContainer,
                this.apiClient,
                this.formatInput,
                index,
                this.contactClient,
                this.captchaInitializer,
                this.onContactValidationCallback.bind(this),
                this.contactCountUpdateCallback.bind(this),
                this.guiCommon,
                this.errorHandler,
                this.mLocalization,
                this.deleteFromContactList.bind(this),
                this.displayRefreshFailWarning.bind(this),
                this.cardViewModelPrefix,
                this.createContactsOnFormSubmit,
                this.addContactIdToDeleted.bind(this),
                null,
            );

            const model: IPostalAddressViewModel = {
                type: elementJq.find(".contact-type").val() as string,
                name: elementJq.find(".contact-name").val() as string,
                street: elementJq.find(".contact-street").val() as string,
                postalCode: elementJq.find(".contact-code").val() as string,
                city: elementJq.find(".contact-city").val() as string,
                country: elementJq.find(".contact-country").val() as string,
                id: elementJq.find(".address-id").val() as number,
            };
            contactCard.setPostalAddressModel(model);
            contactCard.init();
        });
    }

    private deleteFromContactList(contactType: ContactType, id: number): void {
        if (contactType === ContactType.Email) {
            this.emailList = this.emailList.filter((element) => element.id !== id);
        } else if (contactType === ContactType.PhoneNumber) {
            this.phoneNumberList = this.phoneNumberList.filter((element) => element.id !== id);
        }
    }

    private updateContactInList(contactList: IContact[], value: string, newId: number, id: number, index: number, isVerified: boolean, group: string,
                                name: string): void {
        const contact = contactList.find((element) => element.contactIndex.toString() === index.toString());
        if (contact) {
            contact.value = value;
            contact.id = newId;
            contact.contactIndex = index;
            contact.isVerified = isVerified;
            contact.group = group;
            contact.name = name;
        }
    }

    private updateContactIdInList(contactList: IContact[], newId: number, id: number): void {
        const contact = contactList.find((element) => element.id === id);
        if (contact) {
            contact.id = newId;
        }
    }

    private addContactToList(contactList: IContact[], value: string, id: number, contactIndex: number, isVerified: boolean, group: string, name: string): void {
        contactList.push({
            id,
            value,
            contactIndex,
            isVerified,
            group,
            name,
        });
    }

    private createContactFailCallback(alertContainer: JQuery, errorMessages: string[]): void {
        this.guiCommon.destroyNotification(alertContainer);
        this.guiCommon.showNotification(new NotificationConfiguration({
            container: alertContainer,
            notificationType: NotificationType.Danger,
            messages: errorMessages,
        }));
    }

    private isContactUnique(contactType: ContactType, value: string, contactIndex: number): boolean {
        let matchingContacts: IContact[] = [];
        if (contactType === ContactType.Email) {
            matchingContacts = this.emailList.filter((contact, index) => contact.value.toLowerCase() === value.toLowerCase() &&
                contact.contactIndex !== contactIndex);
        } else if (contactType === ContactType.PhoneNumber) {
            matchingContacts = this.phoneNumberList.filter((contact, index) => {
                return contact.value.replace(/\s/g, "") === value.replace(/\s/g, "") && contact.contactIndex !== contactIndex;
            });
        }
        return matchingContacts.length === 0;
    }

    private initErrorHandling(): void {
        this.setValidationSettings(".ignore-validation");
    }

    private contactCountUpdateCallback(contactType: ContactType, contactsRow: JQuery, response?: JQuery.jqXHR): void {
        this.displayRefreshFailWarning(response);
        const contactValidationConfig = this.contactConfigurationProvider.getContactConfiguration();
        if (contactValidationConfig !== null) {

            if (contactValidationConfig[contactType] &&
                contactValidationConfig[contactType].maxNumberOfContacts &&
                contactsRow.children(".tile:not(.add-contact)").length >=
                contactValidationConfig[contactType].maxNumberOfContacts) {
                contactsRow.find(".add-contact").hide();
            } else {
                contactsRow.find(".add-contact").show();
            }
        }
        contactsRow.children(".tile").each((cardIndex, element) => {
            const cardJq = jq(element);
            cardJq.find("input").each((index, inputEl) => {
                const input = jq(inputEl);
                const oldName = input.attr("name");
                if (oldName) {
                    const newName = input.attr("name").replace(/\d/g, `${cardIndex}`);
                    input.attr("name", newName);
                }

                const inputId = input.attr("id");
                if (inputId) {
                    const newId = inputId.replace(/\d/g, `${cardIndex}`);
                    input.attr("id", newId);
                }
            });
        });
    }

    private getUserId(): number {
        const id = jq(".contact-dialogs #user-id").val() as string;
        return parseInt(id, 10);
    }

    private isContactChanged(contactList: IContact[], value: string, contactCardIndex: number, isVerified: boolean, group: string, name: string): boolean {
        const matchingContacts =
            contactList.filter((contact, index) => contact.value.toLowerCase() === value.toLowerCase()
                && contact.isVerified === isVerified
                && contact.group === group
                && contact.name === name
                && contact.contactIndex.toString() === contactCardIndex.toString());
        return matchingContacts.length === 0;
    }

    private addContactIdToDeleted(contactId: number): void {
        this.deletedContactInput.val(`${this.deletedContactInput.val()},${contactId}`);
    }

    private addEmailModalInit(): void {
        const addEmailModal = this.contactDialogsContainer.find("#addEmailForm");

        addEmailModal.on("show.bs.modal", (event) => {
            this.captchaInitializer.prepareContainer(jq, addEmailModal);
            // edit action dialog is shown by JS -> related target is undefined
            if (event.relatedTarget !== undefined) {
                this.mLocalization.translateAsync("new-email", "ContactViewModel").then((value) => {
                    addEmailModal.find(".modal-title").text(value.result.value);
                });

                const verificationType = ContactVerificationType[jq(".contacts-verification-type").data("value") as string];
                if (verificationType === ContactVerificationType.ByAdministrator) {
                    addEmailModal.find(".is-contact-verified").parent(".form-check").removeClass("d-none");
                } else {
                    addEmailModal.find(".is-contact-verified").parent(".form-check").addClass("d-none");
                }
            }
        });
        addEmailModal.on("hide.bs.modal", () => {
            this.captchaInitializer.cleanupContainer(addEmailModal);
        });

        addEmailModal.find("#emailForm").on("submit", (event) => {
            event.preventDefault();
            ContactUtils.handleLoading(addEmailModal, true, ".loading", ".submit-modal-form-btn");

            const alertContainer = addEmailModal.find(".alert-container");
            if (addEmailModal.find("input, select").valid()) {
                const emailModel: IEmailViewModel = this.fillModelOnSubmit(addEmailModal, ContactType.Email, "Email") as IEmailViewModel;

                if (this.isContactUnique(ContactType.Email, emailModel.email, emailModel.index)) {
                    const captchaCallback = (captchaParams: Array<KeyValuePair<string, string>>) => {
                        let ajax: JQuery.jqXHR;
                        if (!this.createContactsOnFormSubmit) {
                            if (!isNaN(emailModel.index)) {
                                const nthChildIndex = emailModel.index + 1;
                                const originalCard = this.emailsRow.children(`:nth-child(${nthChildIndex})`);
                                const methodTypeAttribute = originalCard.attr("data-contact-method-type");
                                const contactMethodType = methodTypeAttribute === "" ? methodType : parseInt(methodTypeAttribute, 10);
                                ContactUtils.handleLoading(addEmailModal, true, ".loading", ".submit-modal-form-btn");
                                if (contactMethodType === MethodContactType.Permanent || this.isContactChanged(this.emailList,
                                    emailModel.email, emailModel.index, emailModel.isContactVerified, emailModel.group, emailModel.name)) {
                                    ajax = this.contactClient.changeContact(emailModel.id,
                                        this.getUserId(),
                                        emailModel,
                                        ContactType.Email,
                                        captchaParams);

                                    ajax.done((response) => {
                                        addEmailModal.find("#EmailId").val(response.id);
                                        jq(`#${emailModel.id}`).trigger("contact-changed", (addEmailModal) as any);
                                        this.updateContactInList(this.emailList, emailModel.email, response.id, emailModel.id,
                                            emailModel.index, emailModel.isContactVerified, emailModel.group, emailModel.name);
                                        this.displayRefreshFailWarning(response);
                                        jq(document.documentElement).trigger("contacts-changed");
                                    });
                                } else {
                                    originalCard.trigger("contact-changed", (addEmailModal) as any);
                                    jq(document.documentElement).trigger("contacts-changed");
                                    this.updateContactInList(this.emailList, emailModel.email, emailModel.id, emailModel.id, emailModel.index,
                                        emailModel.isContactVerified, emailModel.group, emailModel.name);
                                }
                            } else {
                                ajax = this.contactClient.createContact(emailModel, ContactType.Email, captchaParams);

                                ajax.done((response) => {
                                    emailModel.id = response.id;
                                    this.getEmailCard(emailModel, addEmailModal, alertContainer);
                                    this.displayRefreshFailWarning(response);
                                    jq(document.documentElement).trigger("contacts-changed");
                                });
                            }
                        } else {
                            if (!isNaN(emailModel.index)) {
                                const nthChildIndex = emailModel.index + 1;
                                this.emailsRow.children(`:nth-child(${nthChildIndex})`).trigger("contact-changed", (addEmailModal) as any);
                                this.updateContactInList(this.emailList, emailModel.email, emailModel.id, emailModel.id, emailModel.index,
                                    emailModel.isContactVerified, emailModel.group, emailModel.name);
                                this.displayRefreshFailWarning();
                                jq(document.documentElement).trigger("contacts-changed");
                            } else {
                                this.getEmailCard(emailModel, addEmailModal, alertContainer);
                                this.displayRefreshFailWarning();
                                jq(document.documentElement).trigger("contacts-changed");
                            }
                        }

                        if (ajax !== undefined) {
                            ajax.fail((response) => {
                                this.createContactFailCallback(alertContainer,
                                    this.errorHandler.getErrorText(response));
                                this.captchaInitializer.prepareContainer(jq, addEmailModal);
                            });
                            ajax.always(() => {
                                ContactUtils.handleLoading(addEmailModal, false, ".loading", ".submit-modal-form-btn");
                            });
                        } else {
                            ContactUtils.handleLoading(addEmailModal, false, ".loading", ".submit-modal-form-btn");
                        }
                    };

                    this.captchaInitializer.initForAjax(jq, addEmailModal, captchaCallback, "createContact");
                } else {
                    this.mLocalization.translateAsync("email-not-unique", "AuthServiceErrorCode")
                        .then((notUniqueTranslation) => {
                            this.createContactFailCallback(alertContainer, [notUniqueTranslation.result.value]);
                            ContactUtils.handleLoading(addEmailModal, false, ".loading", ".submit-modal-form-btn");
                        });
                }
            } else {
                ContactUtils.handleLoading(addEmailModal, false, ".loading", ".submit-modal-form-btn");
            }
        });
    }

    private addPhoneModalInit(): void {
        const addPhoneModal = this.contactDialogsContainer.find("#addPhoneNumberForm");

        addPhoneModal.on("show.bs.modal", (event) => {
            this.captchaInitializer.prepareContainer(jq, addPhoneModal);
            // edit action dialog is shown by JS -> related target is undefined
            if (event.relatedTarget !== undefined) {
                this.mLocalization.translateAsync("new-phone-number", "ContactViewModel").then((value) => {
                    addPhoneModal.find(".modal-title").text(value.result.value);
                });

                const verificationType = ContactVerificationType[jq(".contacts-verification-type").data("value") as string];
                if (verificationType === ContactVerificationType.ByAdministrator) {
                    addPhoneModal.find(".is-contact-verified").parent(".form-check").removeClass("d-none");
                } else {
                    addPhoneModal.find(".is-contact-verified").parent(".form-check").addClass("d-none");
                }
            }
        });
        addPhoneModal.on("hide.bs.modal", () => {
            this.captchaInitializer.cleanupContainer(addPhoneModal);
        });

        addPhoneModal.find("#phoneForm").on("submit", (event) => {
            event.preventDefault();
            ContactUtils.handleLoading(addPhoneModal, true, ".loading", ".submit-modal-form-btn");

            const alertContainer = addPhoneModal.find(".alert-container");
            if (addPhoneModal.find("input, select").valid()) {
                const phoneModel: IPhoneNumberViewModel = this.fillModelOnSubmit(addPhoneModal, ContactType.PhoneNumber, "Phone") as IPhoneNumberViewModel;

                if (this.isContactUnique(ContactType.PhoneNumber, phoneModel.phoneNumber, phoneModel.index)) {
                    let promise: JQueryPromise<any>;

                    const captchaCallback = (captchaParams: Array<KeyValuePair<string, string>>) => {
                        if (!this.createContactsOnFormSubmit) {
                            if (!isNaN(phoneModel.index)) {
                                const nthChildIndex = phoneModel.index + 1;
                                const originalCard = this.phoneNumberRow.children(`:nth-child(${nthChildIndex})`);
                                const methodTypeAttribute = originalCard.attr("data-contact-method-type");
                                const contactMethodType = methodTypeAttribute === "" ? methodType : parseInt(methodTypeAttribute, 10);
                                if (contactMethodType === MethodContactType.Permanent || this.isContactChanged(this.phoneNumberList,
                                    phoneModel.phoneNumber, phoneModel.index, phoneModel.isContactVerified, phoneModel.group, phoneModel.name)) {
                                    promise = this.contactClient.changeContact(phoneModel.id,
                                        this.getUserId(),
                                        phoneModel,
                                        ContactType.PhoneNumber,
                                        captchaParams);
                                    promise.done((response) => {
                                        addPhoneModal.find("#PhoneId").val(response.id);
                                        jq(`#${phoneModel.id}`).trigger("contact-changed", (addPhoneModal) as any);
                                        this.updateContactInList(this.phoneNumberList, phoneModel.phoneNumber, response.id, phoneModel.id,
                                            phoneModel.index, phoneModel.isContactVerified, phoneModel.group, phoneModel.name);
                                        this.displayRefreshFailWarning(response);
                                        jq(document.documentElement).trigger("contacts-changed");
                                    });
                                } else {
                                    originalCard.trigger("contact-changed", (addPhoneModal) as any);
                                    jq(document.documentElement).trigger("contacts-changed");
                                    this.updateContactInList(this.phoneNumberList, phoneModel.phoneNumber, phoneModel.id, phoneModel.id,
                                        phoneModel.index, phoneModel.isContactVerified, phoneModel.group, phoneModel.name);
                                }
                            } else {
                                promise = this.contactClient.createContact(phoneModel,
                                    ContactType.PhoneNumber,
                                    captchaParams);
                                promise.done((response) => {
                                    phoneModel.id = response.id;
                                    this.getPhoneCard(phoneModel, addPhoneModal, alertContainer);
                                    this.displayRefreshFailWarning(response);
                                    jq(document.documentElement).trigger("contacts-changed");
                                });
                            }
                        } else {
                            if (!isNaN(phoneModel.index)) {
                                const nthChildIndex = phoneModel.index + 1;
                                this.phoneNumberRow.children(`:nth-child(${nthChildIndex})`).trigger("contact-changed", (addPhoneModal) as any);
                                this.updateContactInList(this.phoneNumberList, phoneModel.phoneNumber, phoneModel.id, phoneModel.id, phoneModel.index,
                                    phoneModel.isContactVerified, phoneModel.group, phoneModel.name);
                                this.displayRefreshFailWarning();
                                jq(document.documentElement).trigger("contacts-changed");
                            } else {
                                this.getPhoneCard(phoneModel, addPhoneModal, alertContainer);
                                this.displayRefreshFailWarning();
                                jq(document.documentElement).trigger("contacts-changed");
                            }
                        }

                        if (promise !== undefined) {
                            promise.fail((response) => {
                                this.createContactFailCallback(alertContainer,
                                    this.errorHandler.getErrorText(response));
                                this.captchaInitializer.prepareContainer(jq, addPhoneModal);
                            });

                            promise.always(() => {
                                ContactUtils.handleLoading(addPhoneModal, false, ".loading", ".submit-modal-form-btn");
                            });
                        }
                    };
                    this.captchaInitializer.initForAjax(jq, addPhoneModal, captchaCallback, "createContact");
                } else {
                    this.mLocalization.translateAsync("phone-not-unique", "AuthServiceErrorCode")
                        .then((notUniqueTranslation) => {
                            this.createContactFailCallback(alertContainer, [notUniqueTranslation.result.value]);
                            ContactUtils.handleLoading(addPhoneModal, false, ".loading", ".submit-modal-form-btn");
                        });
                }
            } else {
                ContactUtils.handleLoading(addPhoneModal, false, ".loading", ".submit-modal-form-btn");
            }
        });
    }

    private addPostalAddressModalInit(): void {
        const addPostalAddressModal = this.contactDialogsContainer.find("#addPostalAddressForm");

        addPostalAddressModal.on("show.bs.modal", (event) => {
            // edit action dialog is shown by JS -> related target is undefined
            if (event.relatedTarget !== undefined) {
                this.mLocalization.translateAsync("new-address", "PostalAddressViewModel").then((value) => {
                    addPostalAddressModal.find(".modal-title").text(value.result.value);
                });
            }
        });

        addPostalAddressModal.find("#postalAddressForm").on("submit", (event) => {
            event.preventDefault();
            ContactUtils.handleLoading(addPostalAddressModal, true, ".loading", ".submit-modal-form-btn");
            const alertContainer = addPostalAddressModal.find(".alert-container");
            if (addPostalAddressModal.find("input, select").valid()) {
                if (addPostalAddressModal.find("#index").val()) {
                    const changedCard = this.postalAddressesRow.children().get(addPostalAddressModal.find("#index").val() as number);
                    jq(changedCard).trigger("contact-changed", (addPostalAddressModal) as any);
                    return;
                }

                const existingContacts = this.postalAddressesRow.children(".tile:not(.add-contact)");
                const lastItemIndex = this.getLastItemIndexInRow(existingContacts);

                const postalAddressViewModel: IPostalAddressViewModel = {
                    type: addPostalAddressModal.find("#PostalAddressContactType").val() as string,
                    name: addPostalAddressModal.find("#PostalAddressContactName").val() as string,
                    street: addPostalAddressModal.find("#Street").val() as string,
                    postalCode: addPostalAddressModal.find("#PostalCode").val() as string,
                    city: addPostalAddressModal.find("#City").val() as string,
                    country: addPostalAddressModal.find("#Country").val() as string,
                    id: addPostalAddressModal.find(".address-id").val() as number,
                };

                if (methodType === MethodContactType.Permanent) {
                    const formJq = jq(event.currentTarget);
                    const userId = this.getUserId();
                    formJq.find(".user-data-select-input")
                        .val(addPostalAddressModal.find("#PostalAddressContactType").val() as string);

                    const promise = this.contactClient.createAddress(userId, formJq);
                    promise.done((response) => {
                        postalAddressViewModel.id = response.id;
                        this.createAddressCard(postalAddressViewModel, lastItemIndex, addPostalAddressModal);
                        addPostalAddressModal.find("input").val("");
                        addPostalAddressModal.modal("hide");
                        this.displayRefreshFailWarning(response);
                    });
                    promise.fail((response) => {
                        this.createContactFailCallback(alertContainer,
                            this.errorHandler.getErrorText(response));
                    });
                    promise.always(() => {
                        ContactUtils.handleLoading(addPostalAddressModal, false, ".loading", ".submit-modal-form-btn");
                    });
                } else {
                    this.createAddressCard(postalAddressViewModel, lastItemIndex, addPostalAddressModal);
                    addPostalAddressModal.find("input").val("");
                    addPostalAddressModal.modal("hide");
                    ContactUtils.handleLoading(addPostalAddressModal, false, ".loading", ".submit-modal-form-btn");
                }
            } else {
                ContactUtils.handleLoading(addPostalAddressModal, false, ".loading", ".submit-modal-form-btn");
            }
        });
    }

    private fillModelOnSubmit(elementJq: JQuery, contactType: ContactType, classPrefix: string): IEmailViewModel | IPhoneNumberViewModel {
        return {
            group: elementJq.find(`#${classPrefix}ContactType`).val() as string,
            name: elementJq.find(`#${classPrefix}ContactName`).val() as string,
            ...(contactType === ContactType.PhoneNumber ? {
                    phoneNumber: elementJq.find("#PhoneNumber").val() as string,
                } : {
                    email: elementJq.find("#Email").val() as string,
                }
            ),
            id: parseInt(elementJq.find(`#${classPrefix}Id`).val() as string, 10),
            userId: jq(".contact-dialogs #user-id").val() as string,
            confirmCode: null,
            index: parseInt(elementJq.find("#index").val() as string, 10),
            isContactVerified: elementJq.find(".is-contact-verified").is(":checked"),
            contactVerificationType: ContactVerificationType[elementJq.find(".verification-type").val() as string],
        };
    }

    private fillModelOnInit(elementJq: JQuery, index: number, isPhoneModel: boolean): IEmailViewModel | IPhoneNumberViewModel {
        return {
            id: parseInt(elementJq.find(".contact-id").val() as string, 10),
            ...(isPhoneModel ? {
                    phoneNumber: elementJq.find(".contact-value").val() as string,
                } : {
                    email: elementJq.find(".contact-value").val() as string,
                }
            ),
            group: elementJq.find(".contact-group").val() as string,
            name: elementJq.find(".contact-name").val() as string,
            userId: jq(".contact-dialogs #user-id").val() as string,
            confirmCode: null,
            index,
            isContactVerified: elementJq.find(".verified-selector").is(":checked"),
            contactVerificationType: ContactVerificationType[elementJq.find(`.verification-type`).val() as string],
        };
    }

    public getRecapTableRows(): JQuery {
        let returnRows = jq();
        if (this.emailsRow.children(":not(.add-contact)").length > 0) {
            const emailTableRow = jq("<tr>");
            emailTableRow.append(`<th>${this.emailsRow.prev().text()}</th>`);
            emailTableRow.append(`<td colspan="2"></td>`);
            const emailsRowClone = this.emailsRow.clone();
            emailsRowClone.find(".add-contact").remove();
            emailsRowClone.find(".tile").removeClass("col-sm-6 col-lg-4");
            emailTableRow.find("td").append(emailsRowClone);
            returnRows = returnRows.add(emailTableRow);
        }
        if (this.phoneNumberRow.children(":not(.add-contact)").length > 0) {
            const phoneTableRow = jq("<tr>");
            phoneTableRow.append(`<th>${this.phoneNumberRow.prev().text()}</th>`);
            phoneTableRow.append(`<td colspan="2"></td>`);
            const phoneRowClone = this.phoneNumberRow.clone();
            phoneRowClone.find(".add-contact").remove();
            phoneRowClone.find(".tile").removeClass("col-sm-6 col-lg-4");
            phoneTableRow.find("td").append(phoneRowClone);
            returnRows = returnRows.add(phoneTableRow);
        }
        returnRows.find(".contact-buttons, button").remove();
        return returnRows;
    }
}
